/*
 * <ght-tabs> (sections/grig-home-tabs.liquid), loaded by the section only (defer, before assets/grig-home.js).
 * Port of NaturLabs MAIN assets/nl-w2-tabs-rail.js (<nl-w2-tabs>), same behaviour and timings:
 *   [data-ght-tablist] > button[data-ght-tab][role=tab][aria-controls][data-url] + span[data-ght-ink]
 *   [data-ght-panel] (role=tabpanel, hidden when inactive) > <gh-rail id> > [data-rail-track]
 *   <gh-rail-nav> (assets/grig-home.js) gets the active rail on every change (arrows + meter)
 *   a[data-ght-view-all] follows the active collection (only when the section setting asks for it)
 *
 * ARIA tabs with automatic activation (arrows, Home, End, roving tabindex). The ink glides with one transform
 * (520 ms); panels cross fade in one grid cell (WAAPI: out 240 ms, in 560 ms with a 12 px rise) and the first six
 * cards of the new panel arrive from 32 px with a 50 ms stagger (640 ms). The tab strip centres the active tab and
 * the new rail starts at its beginning. Reduced motion or the theme motion switch: instant switch.
 * Theme editor: selecting a tab block activates it. Emits 'ght-tabs:change' { index, tab, panel } (bubbles).
 */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('ght-tabs')) return;

  var EASE = 'cubic-bezier(0.2, 0.7, 0.2, 1)';
  var root = document.documentElement;
  var mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function reduced() {
    if (window.GRIGHome && typeof window.GRIGHome.reduced === 'function') return window.GRIGHome.reduced();
    return mqReduced.matches || root.classList.contains('gh-motion-off') || root.getAttribute('data-grig-motion') === 'false';
  }

  class GHTabs extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-ght-panel]'));
      if (!this.panels.length) return;
      this._bound = true;
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-ght-tab]'));
      this.list = this.querySelector('[data-ght-tablist]');
      this.ink = this.querySelector('[data-ght-ink]');
      this.viewAll = this.querySelector('[data-ght-view-all]');
      this.nav = this.querySelector('gh-rail-nav');
      this.index = Math.max(0, this.tabs.findIndex(function (tab) { return tab.getAttribute('aria-selected') === 'true'; }));

      var self = this;
      this.onClick = function (event) {
        var i = self.tabs.indexOf(event.currentTarget);
        if (i >= 0) self.select(i, true);
      };
      this.onKeydown = function (event) {
        var i = self.tabs.indexOf(event.currentTarget);
        if (i >= 0) self.keydown(event, i);
      };
      this.tabs.forEach(function (tab) {
        tab.addEventListener('click', self.onClick);
        tab.addEventListener('keydown', self.onKeydown);
      });

      if (this.list && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(function () { self.placeInk(false); });
        this.resizeObserver.observe(this.list);
      }

      this.onBlockSelect = function (event) {
        var tab = event.target && event.target.closest ? event.target.closest('[data-ght-tab]') : null;
        var i = self.tabs.indexOf(tab);
        if (i >= 0) self.select(i, false);
      };
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      // The nav is upgraded by grig-home.js (loaded after this file); never set .rail on an unupgraded element,
      // the own property would shadow the class setter.
      customElements.whenDefined('gh-rail-nav').then(function () { self.bindNav(); });

      this.placeInk(false);
      // Fonts change the tab widths: place the ink again once they are ready
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.placeInk(false); });
      requestAnimationFrame(function () { self.setAttribute('data-ready', ''); });
    }

    disconnectedCallback() {
      if (!this._bound) return;
      var self = this;
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.tabs.forEach(function (tab) {
        tab.removeEventListener('click', self.onClick);
        tab.removeEventListener('keydown', self.onKeydown);
      });
      this.finishSwitch();
      this._bound = false;
    }

    get activePanel() {
      return this.panels[this.index] || null;
    }

    bindNav() {
      var panel = this.activePanel;
      if (!this.nav || !panel || !customElements.get('gh-rail-nav')) return;
      var rail = panel.querySelector('gh-rail');
      if (rail && this.nav.rail !== rail) this.nav.rail = rail;
    }

    placeInk(animate) {
      var tab = this.tabs[this.index];
      if (!this.ink || !tab) return;
      var transform = 'translate3d(' + tab.offsetLeft + 'px, 0, 0) scaleX(' + tab.offsetWidth + ')';
      if (animate && !reduced()) {
        this.ink.style.transform = transform;
        return;
      }
      var transition = this.ink.style.transition;
      this.ink.style.transition = 'none';
      this.ink.style.transform = transform;
      void this.ink.offsetWidth;
      this.ink.style.transition = transition;
    }

    revealTab() {
      var tab = this.tabs[this.index];
      var list = this.list;
      if (!list || !tab) return;
      if (list.scrollWidth - list.clientWidth <= 2) return;
      var left = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
      list.scrollTo({ left: Math.max(0, left), behavior: reduced() ? 'auto' : 'smooth' });
    }

    syncLink() {
      var tab = this.tabs[this.index];
      if (this.viewAll && tab && tab.dataset.url) this.viewAll.setAttribute('href', tab.dataset.url);
    }

    finishSwitch() {
      if (this.outAnimation) {
        this.outAnimation.onfinish = null;
        this.outAnimation.cancel();
        this.outAnimation = null;
      }
      if (this.leaving) {
        this.leaving.hidden = true;
        this.leaving.removeAttribute('data-leaving');
        this.leaving = null;
      }
    }

    select(index, fromUser) {
      var target = this.panels[index];
      if (!target) return;
      if (index === this.index) {
        if (fromUser && this.tabs[index]) this.tabs[index].focus();
        return;
      }

      this.finishSwitch();
      var from = this.activePanel;
      this.index = index;

      this.tabs.forEach(function (tab, i) {
        var selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      if (fromUser && this.tabs[index]) this.tabs[index].focus({ preventScroll: true });

      target.hidden = false;
      var track = target.querySelector('[data-rail-track]');
      if (track) {
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = 0;
        track.style.scrollBehavior = '';
      }

      this.placeInk(true);
      this.revealTab();
      this.syncLink();
      this.bindNav();

      if (!from || reduced() || typeof target.animate !== 'function') {
        if (from) from.hidden = true;
      } else {
        var self = this;
        this.leaving = from;
        from.setAttribute('data-leaving', '');
        this.outAnimation = from.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: 240,
          easing: 'ease-out',
          fill: 'forwards'
        });
        this.outAnimation.onfinish = function () { self.finishSwitch(); };

        target.animate(
          [
            { opacity: 0, transform: 'translate3d(0, 12px, 0)' },
            { opacity: 1, transform: 'none' }
          ],
          { duration: 560, easing: EASE }
        );
        if (track) {
          // Mandatory snapping follows the shifted first card (snap areas include transforms) and would scroll the
          // rail by 32 px while the cards arrive; pause snapping until the stagger is done so the rail stays at 0.
          var restoreSnap = function () { track.style.scrollSnapType = ''; };
          track.style.scrollSnapType = 'none';
          var arrivals = Array.prototype.slice.call(track.children, 0, 6).map(function (item, i) {
            return item.animate(
              [
                { opacity: 0, transform: 'translate3d(32px, 0, 0)' },
                { opacity: 1, transform: 'none' }
              ],
              { duration: 640, delay: 50 * i, easing: EASE, fill: 'backwards' }
            );
          });
          var lastArrival = arrivals[arrivals.length - 1];
          if (lastArrival && lastArrival.finished) lastArrival.finished.then(restoreSnap, restoreSnap);
          else restoreSnap();
        }
      }

      this.dispatchEvent(new CustomEvent('ght-tabs:change', {
        bubbles: true,
        detail: { index: index, tab: this.tabs[index] || null, panel: target }
      }));
    }

    keydown(event, index) {
      var last = this.tabs.length - 1;
      var map = {
        ArrowRight: index === last ? 0 : index + 1,
        ArrowLeft: index === 0 ? last : index - 1,
        Home: 0,
        End: last
      };
      if (!(event.key in map)) return;
      event.preventDefault();
      this.select(map[event.key], true);
    }
  }

  customElements.define('ght-tabs', GHTabs);
})();
